import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { dashApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldAlert, Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { AuthDisclaimer } from "@/components/AuthDisclaimer";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Veuillez saisir votre e-mail");
    setLoading(true);
    try {
      await dashApi.forgotPassword(email.trim());
      setSent(true);
    } catch {
      toast.error("Une erreur est survenue, veuillez réessayer");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden bg-[#0B1120]">
      <div className="absolute inset-0 opacity-30 pointer-events-none"
        style={{ backgroundImage: "radial-gradient(circle at 20% 20%, rgba(16,185,129,0.15), transparent 40%), radial-gradient(circle at 80% 80%, rgba(234,179,8,0.12), transparent 40%)" }} />

      <div className="max-w-md w-full bg-slate-900/90 border border-slate-800 rounded-2xl shadow-2xl p-8 backdrop-blur-xl relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 flex">
          <div className="flex-1 bg-emerald-600" />
          <div className="flex-1 bg-yellow-500" />
          <div className="flex-1 bg-red-600" />
        </div>

        <div className="flex items-center justify-center gap-3 mb-6 pb-5 border-b border-slate-800">
          <div className="w-12 h-12 rounded-xl bg-emerald-950 border border-emerald-500/30 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-emerald-400" />
          </div>
          <div className="text-left">
            <h1 className="font-display text-xl font-bold text-slate-100 leading-tight">Sama Jaamu</h1>
            <p className="text-xs font-mono uppercase tracking-wider text-slate-400">Mot de passe oublié</p>
          </div>
        </div>

        {sent ? (
          <div className="text-center py-4" data-testid="forgot-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <p className="text-slate-100 font-display font-semibold">E-mail envoyé</p>
            <p className="text-sm text-slate-400 mt-2">
              Si un compte est associé à <strong className="text-slate-200">{email}</strong>, vous recevrez un lien de réinitialisation. Le lien expire dans 1 heure.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-sm text-slate-400">Saisissez l'e-mail de votre compte. Nous vous enverrons un lien pour réinitialiser votre mot de passe.</p>
            <div className="space-y-2">
              <Label className="text-xs font-mono uppercase tracking-wider text-slate-400">Adresse e-mail</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="pl-10 bg-slate-950 border-slate-700 text-slate-100 h-11" placeholder="manager@samajaamu.sn" required />
              </div>
            </div>
            <Button type="submit" data-testid="forgot-submit-btn" disabled={loading} className="w-full h-11 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer le lien de réinitialisation"}
            </Button>
          </form>
        )}

        <AuthDisclaimer />

        <div className="mt-6 text-center">
          <button type="button" data-testid="back-to-login-link" onClick={() => navigate("/login")} className="text-xs font-mono text-slate-400 hover:text-slate-200 inline-flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
